import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Yup from 'yup';
import {
  Modal,
  ModalButtons,
  ModalContent,
  Tab,
  TabItem,
  Button,
  ProgressBar,
  withForm,
} from 'mw-style-react';
import AccessRules from '@control-front-end/common/components/AccessRules';
import {
  CREATE_SCRIPT,
  UPDATE_SCRIPT,
  UPDATE_SCRIPT_ENV,
} from '@control-front-end/common/constants/scripts';
import { DEL_MODAL } from 'constants';
import { RE_APP_SHORT_NAME } from '@control-front-end/common/constants/regExp';
import useIntl from 'useIntl';
import mes from './intl';
import ScriptSettings from './components/ScriptSettings';
// eslint-disable-next-line no-unused-vars
import m from '../../Modal.scss'; // NOSONAR
// eslint-disable-next-line no-unused-vars
import l from './CreateScriptApp.scss'; // NOSONAR

function CreateScriptAppBase(props) {
  const {
    data = {},
    values,
    handleSubmit,
    handleOnChange,
    onContentChange,
    visibility,
    onClose,
    isSubmit,
    errors,
  } = props;
  const t = useIntl();
  const auth = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState('settings');
  const initialAccess = [
    {
      avatar: auth.avatar,
      name: auth.nick,
      userId: auth.id,
      privs: {
        view: true,
        modify: true,
        remove: true,
      },
    },
  ];

  return (
    <Modal
      styleName="m.modal l.modal"
      size="xlarge"
      visibility={visibility}
      onClose={onClose}
      label={data.id ? t(mes.editScriptApp) : t(mes.createScriptApp)}
    >
      <ModalContent styleName="m.modal__content l.modal__content">
        <div styleName="l.content__wrap">
          <Tab
            value={activeTab}
            type="auto"
            underline={true}
            onChange={({ value }) => setActiveTab(value)}
          >
            <TabItem label={t(mes.settings)} value="settings" />
            <TabItem label={t(mes.scriptAccess)} value="access" />
          </Tab>
          <div styleName="l.content">
            {activeTab === 'access' ? (
              <AccessRules
                styleType="embeded"
                rules={values.access || initialAccess}
                ownerId={auth.id}
                onChange={(access) =>
                  handleOnChange({ id: 'access', value: access })
                }
              />
            ) : (
              <ScriptSettings
                values={values}
                errors={errors}
                handleOnChange={(scriptData) => {
                  handleOnChange(scriptData);
                  onContentChange();
                }}
              />
            )}
          </div>
        </div>
      </ModalContent>
      <ModalButtons styleName="m.modal__buttons">
        <Button
          label={data.id ? t(mes.save) : t(mes.create)}
          size="large"
          fontWeight="normal"
          onClick={handleSubmit}
          visibility={isSubmit ? 'disabled' : 'visible'}
        />
        <ProgressBar
          styleName="m.modal__loader"
          type="circle"
          size="small"
          visibility={isSubmit ? 'visible' : 'hidden'}
        />
      </ModalButtons>
    </Modal>
  );
}

CreateScriptAppBase.propTypes = {
  data: PropTypes.object,
  visibility: PropTypes.bool,
  onClose: PropTypes.func,
  handleSubmit: PropTypes.func,
  handleOnChange: PropTypes.func,
  onContentChange: PropTypes.func,
  isSubmit: PropTypes.bool,
  values: PropTypes.object,
  errors: PropTypes.object,
};

const CreateScriptApp = withForm(
  {
    mapPropsToValues(props) {
      if (!props.data || !props.data.id) return {};
      const {
        title,
        shortName,
        description,
        sharedWith,
        picture,
        envs,
        access,
      } = props.data;
      const develop = envs.find((i) => i.title === 'develop');
      const production = envs.find((i) => i.title === 'production');
      const {
        apiLogin,
        apiSecret,
        companyId,
        procId: devProcId,
      } = develop.corezoidCredentials;
      const { procId: prodProcId } = production.corezoidCredentials;
      return {
        title,
        shortName,
        description,
        sharedWith,
        picture,
        access,
        apiLogin,
        apiSecret,
        companyId,
        devProcId,
        prodProcId,
      };
    },
    yup: Yup.object().shape({
      title: Yup.string().min(2).required('field_required'),
      shortName: Yup.string()
        .matches(new RegExp(RE_APP_SHORT_NAME), 'wrong_format')
        .required('field_required'),
      description: Yup.string().nullable(true),
      sharedWith: Yup.string(),
      picture: Yup.string().nullable(true),
      apiLogin: Yup.string().required('field_required'),
      apiSecret: Yup.string().required('field_required'),
      companyId: Yup.string().required('field_required'),
      devProcId: Yup.string().required('field_required'),
      prodProcId: Yup.string().required('field_required'),
    }),
    handleSubmit: (values, dispatch, formActions, props) => {
      const { data } = props;
      formActions.callback = props.callback;
      const {
        title,
        shortName,
        description,
        sharedWith,
        picture,
        access,
        apiLogin,
        apiSecret,
        companyId,
        devProcId,
        prodProcId,
      } = values;
      const credentials = { apiLogin, apiSecret, companyId };
      const corezoidCredentials = {
        develop: { ...credentials, procId: devProcId },
        production: { ...credentials, procId: prodProcId },
      };
      if (data.id) {
        dispatch({
          type: UPDATE_SCRIPT.REQUEST,
          payload: {
            id: data.id,
            title,
            shortName,
            description,
            sharedWith,
            picture,
            access,
          },
          formActions,
          callback: () => {
            data.envs.forEach((env) => {
              const envIsChanged = Object.keys(
                corezoidCredentials[env.title]
              ).some(
                (key) =>
                  corezoidCredentials[env.title][key] !==
                  env.corezoidCredentials[key]
              );
              if (envIsChanged) {
                dispatch({
                  type: UPDATE_SCRIPT_ENV.REQUEST,
                  payload: {
                    scriptId: data.id,
                    envId: env.id,
                    credentials: corezoidCredentials[env.title],
                  },
                });
              }
            });
            dispatch({ type: DEL_MODAL });
          },
        });
      } else {
        dispatch({
          type: CREATE_SCRIPT.REQUEST,
          payload: {
            title,
            shortName,
            description,
            sharedWith,
            picture,
            corezoidCredentials,
            access,
          },
          formActions,
          callback: () => dispatch({ type: DEL_MODAL }),
        });
      }
    },
  },
  CreateScriptAppBase
);

export default CreateScriptApp;
